import * as React from 'react';

import { Res, BuyerCustomerDTO, CustomerInListDTO, CustomerDetailsDTO, CustomerTypeInListDTO, CustomerForEditionDTO } from 'core/api';
import * as UI from 'core/ui';
import { Content, Header, Footer, PosScreen, PhoneOnly, BackActionButton, Filler, LargeDeviceOnly, AcceptActionButton, ActionButton, ThemeManager } from 'pos/ui';
import * as Images from 'pos/Images';
import { CustomerFacade } from 'pos/facades';
import { Command, Cordova } from 'pos/common';
import selectParentCustomer from './selectParentCustomer';
import { BarcodeReader, BasicButton, SvgIcon } from 'core/ui-next';
import { QRReader } from 'pos/services';
import { Message, Modal } from 'core/ui-next/modal';
import { HStack, VStack, Text } from '@chakra-ui/react';
import { FormRef, useControlsFor } from 'core/ui-next/controls';
import { Close } from 'core/img';
import { z } from 'zod';
import { PhoneSearchCustomerTable } from './searchCustomerTablePhone';
import { LargeSearchCustomerTable } from './searchCustomerTable';

type Props = {
	initialCriteria: string;
	initialCustomers: CustomerInListDTO[];
	filterByCustomerType: Nullable<CustomerTypeInListDTO>;
	requirePhoneNumber: boolean;
	showNotes: boolean;
} & Modal.Actions<BuyerCustomerDTO>;

const stateSchema = z.object({
	criteria: z.string()
});
type FormState = z.infer<typeof stateSchema>;

const MAX_CUSTOMERS_TO_FIND = 200;

function SearchCustomerScreen(props: Props): React.ReactNode {


	const { initialCriteria, initialCustomers, filterByCustomerType, requirePhoneNumber, showNotes, cancel, close } = props;

	const [criteria, setCriteria] = React.useState<string>(initialCriteria);
	const [customers, setCustomers] = React.useState<CustomerInListDTO[]>(initialCustomers);
	const [selectedCustomer, setSelectedCustomer] = React.useState<Nullable<CustomerInListDTO>>(initialCustomers.length === 1 ? initialCustomers[0] : null);
	const [searchResultInfo, setSearchResultInfo] = React.useState('');
	const [loading, setLoading] = React.useState(false);

	const formRef = React.useRef<FormRef<FormState>>(null);
	const { Form, Input } = useControlsFor<FormState>();

	const initialState: FormState = {
		criteria: criteria
	};



	// closeWithCustomer debe ser declarado antes que otros callbacks que lo utilizan, como onCardNumberRead y onAccept
	const closeWithCustomer = React.useCallback(async (customer: CustomerDetailsDTO) => {
		const customerType = filterByCustomerType || customer.Type;
		if (customerType.Id !== customer.Type.Id) {
			await Message.show(Res.Msg.MSG_CUSTOMER_INVALID_TYPE(customerType.Name));
			return;
		}

		if (requirePhoneNumber && !customer.Telephone) {
			await Message.show(Res.Msg.MSG_CUSTOMER_MUST_HAVE_PHONE_NUMBER());
			return;
		}

		if (showNotes && customer.ShowNotes && customer.Notes) {
			await Message.show(customer.Notes);
		}

		close(customer);
	}, [filterByCustomerType, requirePhoneNumber, showNotes, close]);



	// onSearch debe ser declarado antes que onReadQR
	const onSearch = React.useCallback(async () => {
		setLoading(true);
		const result = await formRef.current!.getValue();
		if (!result.ok)
			return;

		const customers = await CustomerFacade.getByText(result.value.criteria, MAX_CUSTOMERS_TO_FIND, filterByCustomerType?.Id || null);
		const selectedCustomer = customers.length === 1 ? customers[0] : null;
		const searchResultInfo =
			customers.length === 0 ? Res.Msg.MSG_NOT_CUSTOMER_FOUND()
				: customers.length >= MAX_CUSTOMERS_TO_FIND ? Res.Msg.MSG_TOO_MANY_CUSTOMERS_FOUND(MAX_CUSTOMERS_TO_FIND)
					: '';

		setCustomers(customers);
		setSelectedCustomer(selectedCustomer);
		setSearchResultInfo(searchResultInfo);
		setLoading(false);
	}, [filterByCustomerType]);



	// onCardNumberRead debe ser declarado antes que useEffect
	const onCardNumberRead = React.useCallback(async (cardNumber: string) => {
		let customer = await CustomerFacade.getDetailsByCardNumber(cardNumber);
		if (customer.Id === 0) {
			await Message.show(Res.Msg.MSG_CUSTOMER_CARD_NOT_FOUND());
			return;
		}

		if (customer.ParentCustomerId > 0) {
			const parentCustomer = await CustomerFacade.getDetails(customer.ParentCustomerId);
			const result = await selectParentCustomer(customer, parentCustomer);

			if (!result.ok)
				return;

			customer = result.value;
		}

		await closeWithCustomer(customer);
	}, [closeWithCustomer]);



	React.useEffect(() => {
		const disposeReader = BarcodeReader.start(onCardNumberRead);
		return () => disposeReader();
	}, [onCardNumberRead]);



	const onReadQR = React.useCallback(async () => {
		const qrResult = await QRReader.read();
		if (!qrResult.ok)
			return;

		setCriteria(qrResult.value);
		async () => {
			formRef.current!.setValue('criteria', qrResult.value);
			await onSearch();
		};

	}, [onSearch]);



	const onCancel = React.useCallback(async () => cancel(), [cancel]);

	const onAccept = React.useCallback(async () => {
		if (!selectedCustomer) {
			await Message.show(Res.Msg.MSG_CUSTOMER_MUST_BE_SELECTED());
			return;
		}

		const customer = await CustomerFacade.getDetails(selectedCustomer!.Id);
		await closeWithCustomer(customer);
	}, [selectedCustomer, closeWithCustomer]);

	const onClear = React.useCallback(async () => {
		setCriteria('');
		setSelectedCustomer(null);
		setCustomers([]);
		formRef.current!.setValue('criteria', '');
	}, []);

	const onSelectCustomer = React.useCallback((customer: CustomerInListDTO) => setSelectedCustomer(customer), []);

	const getUpdatedCustomers = React.useCallback((modifiedCustomer: CustomerInListDTO): CustomerInListDTO[] => {
		const updatedCustomers = [...customers];
		const index = updatedCustomers.indexOf(updatedCustomers.find(c => c.Id === modifiedCustomer.Id)!);
		updatedCustomers[index] = modifiedCustomer!;
		return updatedCustomers;
	}, [customers]);

	const reloadCustomer = React.useCallback(async (newOrUpdatedCustomer: CustomerForEditionDTO): Promise<Nullable<CustomerInListDTO>> => {
		const newSearchResult = await CustomerFacade.getByText(newOrUpdatedCustomer.FiscalName, MAX_CUSTOMERS_TO_FIND, filterByCustomerType?.Id || null);
		return newOrUpdatedCustomer.Id !== 0
			? newSearchResult.filter(x => x.Id === newOrUpdatedCustomer.Id)[0] ?? null
			: newSearchResult.filter(x => x.FiscalName.trim().toLowerCase() === newOrUpdatedCustomer.FiscalName.trim().toLowerCase()).last();
	}, [filterByCustomerType]);

	const IsSelectedCustomer = React.useCallback(async (): Promise<boolean> => {
		if (selectedCustomer === null || selectedCustomer.Id === undefined) {
			await Message.show(Res.Msg.MSG_CUSTOMER_MUST_BE_SELECTED());
			return false;
		}
		return true;
	}, [selectedCustomer]);



	const onCreateCustomer = React.useCallback(async () => {
		const result = await Command.execute('createCustomer');

		if (!result.ok)
			return;

		const insertedCustomer = await reloadCustomer(result.value);
		const updatedCustomers = insertedCustomer === null ? [...customers] : [...customers, insertedCustomer];
		setCustomers(updatedCustomers);
		setSelectedCustomer(insertedCustomer);
	}, [customers, reloadCustomer]);



	const onEditSelectedCustomer = React.useCallback(async () => {
		if (!(await IsSelectedCustomer()))
			return;

		const result = await Command.execute('editCustomer', selectedCustomer!.Id);
		if (!result.ok)
			return;

		const modifiedCustomer = await reloadCustomer(result.value);
		if (modifiedCustomer === null)
			return;

		setCustomers(getUpdatedCustomers(modifiedCustomer));
		setSelectedCustomer(modifiedCustomer);
	}, [IsSelectedCustomer, getUpdatedCustomers, reloadCustomer, selectedCustomer]);



	const onDeleteSelectedCustomer = React.useCallback(async () => {
		if (!(await IsSelectedCustomer()))
			return;

		const result = await Command.execute('deleteCustomer', selectedCustomer!.Id);
		if (result.ok) {
			setCustomers(customers.filter(x => x.Id !== selectedCustomer!.Id));
			setSelectedCustomer(null);
		}
		formRef.current!.setValue('criteria', '');

	}, [IsSelectedCustomer, customers, selectedCustomer]);




	return <PosScreen size={{ width: 1000, height: 720 }} screenId='SEARCH_CUSTOMER' useKeyPad={false} useKeyBoard={true}>
		<Header title={Res.Web.SET_BUYER_CUSTOMER_TITLE()} image={Images.SearchCustomer} onClose={onCancel} />
		<Content>
			<VStack paddingY={ThemeManager.isPhone() ? '4' : '6'} paddingX={ThemeManager.isPhone() ? '2' : '16'} w={'full'}>
				<VStack w={'full'} gap={'9'}>
					<VStack gap={'6'} w={'full'}>
						<Text fontSize="md" fontWeight={'semibold'} w={'full'} textAlign={'center'}>{ThemeManager.isPhone() ? Res.Web.SET_BUYER_CUSTOMER_SEARCH_TITLE() : Res.Web.SET_BUYER_CUSTOMER()}</Text>
						<HStack gap={'9'} w={'full'} justifyContent={'space-around'}>
							<HStack w={'100'}>
								<Form ref={formRef} initialValue={initialState} schema={stateSchema} style={{ width: '100%' }}>
									<Input
										linkTo="criteria"
										placeholder={Res.Web.SET_BUYER_CUSTOMER_SEARCH_PLACEHOLDER()}
										onKeyDown={UI.whenEnterKey(onSearch)}
										startElement={
											<SvgIcon src={Images.Search} width={'4'} height={'4'} cursor={'pointer'} onClick={onSearch} />
										}
										endElement={
											<SvgIcon src={Close} width={'4'} height={'4'} cursor={'pointer'} onClick={onClear} />
										}
										borderRadius={'4xl'}
										borderColor={'neutral.600'}
										height={'10'}
									/>
								</Form>
								{Cordova.isCordovaNavigator() && (
									<HStack padding={'2'}>
										<SvgIcon src={Images.SearchBarcode} width={'6'} height={'6'} cursor={'pointer'} onClick={onReadQR} />
									</HStack>
								)}
							</HStack>
							<LargeDeviceOnly>
								<HStack w={'76.5'} justifyContent={'space-around'} borderRadius={'md'} borderColor={'neutral.500'} borderWidth={'1px'} height={'10'} paddingX={'2'} cursor={'pointer'}>
									<HStack onClick={onDeleteSelectedCustomer}>
										<SvgIcon src={Images.TrashCan} width={'4.5'} height={'4.5'} />
										<Text>{Res.Web.SET_BUYER_CUSTOMER_DELETE_CUSTOMER_BTN()}</Text>
									</HStack>
									<HStack
										borderLeftWidth={'1px'}
										borderRightWidth={'1px'}
										borderColor={'neutral.300'}
										height={'full'}
										paddingX={'4'}
										onClick={onEditSelectedCustomer}>
										<SvgIcon src={Images.Edit} width={'4.5'} height={'4.5'} />
										<Text>{Res.Web.SET_BUYER_CUSTOMER_EDIT_CUSTOMER_BTN()}</Text>
									</HStack>
									<HStack onClick={onCreateCustomer}>
										<SvgIcon src={Images.Plus} width={'4.5'} height={'4.5'} />
										<Text>{Res.Web.SET_BUYER_CUSTOMER_ADD_CUSTOMER_BTN()}</Text>
									</HStack>
								</HStack>
							</LargeDeviceOnly>
						</HStack>
					</VStack>
					<LargeDeviceOnly>
						<LargeSearchCustomerTable
							customers={customers}
							selectedCustomer={selectedCustomer}
							onSelectCustomer={onSelectCustomer}
							loading={loading}
							searchResultInfo={searchResultInfo}
							MAX_CUSTOMERS_TO_FIND={MAX_CUSTOMERS_TO_FIND}
						/>
						<HStack w={'82'} h={'10'} >
							<BasicButton type={'success'} text={Res.Web.SET_BUYER_CUSTOMER_TITLE()} onClick={onAccept} width={'full'} height={'full'} />
						</HStack>
					</LargeDeviceOnly>
					<PhoneOnly>
						<PhoneSearchCustomerTable
							customers={customers}
							selectedCustomer={selectedCustomer}
							onSelectCustomer={onSelectCustomer}
							loading={loading}
							searchResultInfo={searchResultInfo}
							MAX_CUSTOMERS_TO_FIND={MAX_CUSTOMERS_TO_FIND}
						/>
					</PhoneOnly>
				</VStack>
			</VStack>
		</Content>
		<Footer>
			<PhoneOnly>
				<BackActionButton action={onCancel} />
				<ActionButton title={Res.Web.SET_BUYER_CUSTOMER_ADD_CUSTOMER_BTN()} image={Images.Plus} action={onCreateCustomer} />
				<ActionButton title={Res.Web.SET_BUYER_CUSTOMER_EDIT_CUSTOMER_BTN()} image={Images.Edit} action={onEditSelectedCustomer} />
				<ActionButton title={Res.Web.SET_BUYER_CUSTOMER_DELETE_CUSTOMER_BTN()} image={Images.TrashCan} action={onDeleteSelectedCustomer} />
				<Filler />
				<AcceptActionButton action={onAccept} />
			</PhoneOnly>
		</Footer>
	</PosScreen >;
}


type SearchCustomerParams = {
	filterByCustomerType: Nullable<CustomerTypeInListDTO>,
	initialCriteria: string,
	requirePhoneNumber: boolean,
	showCustomerNotes: boolean
}

async function search(params: SearchCustomerParams): Promise<Result<CustomerDetailsDTO>> {

	const initialCustomers = params.filterByCustomerType || params.initialCriteria
		? await CustomerFacade.getByText(params.initialCriteria, MAX_CUSTOMERS_TO_FIND, params.filterByCustomerType?.Id || null)
		: [];

	return Modal.show(SearchCustomerScreen, {
		initialCustomers,
		filterByCustomerType: params.filterByCustomerType || null,
		initialCriteria: params.initialCriteria || '',
		requirePhoneNumber: params.requirePhoneNumber,
		showNotes: params.showCustomerNotes
	});
}

declare global {
	interface CommandType {
		searchCustomerByPhone: (phone: string, showNotes?: 'SHOW_CUSTOMER_NOTES' | 'IGNORE_CUSTOMER_NOTES') => Promise<Result<CustomerDetailsDTO>>
		searchCustomerByText: (text?: string) => Promise<Result<CustomerDetailsDTO>>
		searchCustomerByType: (type: CustomerTypeInListDTO) => Promise<Result<CustomerDetailsDTO>>
	}
}

Command.register('searchCustomerByPhone', 'NO_PRECONDITION', (phone: string, showNotes?: 'SHOW_CUSTOMER_NOTES' | 'IGNORE_CUSTOMER_NOTES'): Promise<Result<CustomerDetailsDTO>> => {
	return search({ filterByCustomerType: null, initialCriteria: phone, requirePhoneNumber: true, showCustomerNotes: showNotes === 'SHOW_CUSTOMER_NOTES' });
});

Command.register('searchCustomerByText', 'NO_PRECONDITION', (text?: string): Promise<Result<CustomerDetailsDTO>> => {
	return search({ filterByCustomerType: null, initialCriteria: text || '', requirePhoneNumber: false, showCustomerNotes: true });
});

Command.register('searchCustomerByType', 'NO_PRECONDITION', (type: CustomerTypeInListDTO): Promise<Result<CustomerDetailsDTO>> => {
	return search({ filterByCustomerType: type, initialCriteria: '', requirePhoneNumber: false, showCustomerNotes: true });
});
